import { useCallback, useEffect, useState } from 'react';

import { useFormWithValidation } from '@splitfiesta/core';

import { commentService } from '@/services/comment/CommentService';

interface UseCommentManagementProps {
  onSubmitComment: (text: string) => Promise<void>;
  onEditComment: (commentId: string, text: string) => Promise<void>;
  onDeleteComment: (commentId: string) => Promise<void>;
}

interface UseCommentManagementReturn {
  // Main comment form
  commentText: string;
  setCommentText: (text: string) => void;
  handleSubmitComment: (e: React.FormEvent) => Promise<void>;
  submitComment: () => Promise<void>;
  isSubmitting: boolean;

  // Edit state management
  editingComment: { [key: string]: boolean };
  editCommentText: { [key: string]: string };
  startEditing: (commentId: string, currentText: string) => void;
  cancelEditing: (commentId: string) => void;
  handleEditSubmit: (commentId: string) => Promise<void>;
  isEditingComment: (commentId: string) => boolean;
  getEditText: (commentId: string) => string;
  setEditText: (commentId: string, text: string) => void;

  // Delete state management
  deletingComment: { [key: string]: boolean };
  handleDeleteComment: (commentId: string) => Promise<void>;
  isDeletingComment: (commentId: string) => boolean;

  // Utilities
  handleKeyDown: (e: React.KeyboardEvent, action: () => void) => void;
}

export function useCommentManagement({
  onSubmitComment,
  onEditComment,
  onDeleteComment,
}: UseCommentManagementProps): UseCommentManagementReturn {
  const [commentText, setCommentText] = useState('');
  const [editingComment, setEditingComment] = useState<{
    [key: string]: boolean;
  }>({});
  const [editCommentText, setEditCommentText] = useState<{
    [key: string]: string;
  }>({});
  const [deletingComment, setDeletingComment] = useState<{
    [key: string]: boolean;
  }>({});

  // Main comment submission form
  const { handleSubmit: handleSubmitCommentRaw, isSubmitting } =
    useFormWithValidation({
      onSubmit: async () => {
        const text = commentText.trim();
        if (!text) return;

        await onSubmitComment(text);
        setCommentText('');
      },
      validateFn: () => {
        const validation = commentService.validateComment(commentText);
        return validation.isValid ? null : validation.error || null;
      },
    });

  // Wrap handleSubmitComment to match the expected signature
  const handleSubmitComment = useCallback(
    async (e: React.FormEvent) => {
      await handleSubmitCommentRaw(e, {});
    },
    [handleSubmitCommentRaw]
  );

  // Direct submit method for keyboard shortcuts
  const submitComment = useCallback(async () => {
    const syntheticEvent = {
      preventDefault: () => {
        // Synthetic event to prevent form submission
      },
    } as React.FormEvent;
    await handleSubmitComment(syntheticEvent);
  }, [handleSubmitComment]);

  // Handle click outside to cancel edit
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (!commentService.shouldCancelEditOnClick(target)) {
        return;
      }

      const activeEdits = commentService.getActiveEditIds(editingComment);

      if (activeEdits.length > 0) {
        const { editingState, textState } = commentService.clearAllEdits(
          editingComment,
          editCommentText
        );
        setEditingComment(editingState);
        setEditCommentText(textState);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingComment, editCommentText]);

  // Start editing a comment
  const startEditing = useCallback(
    (commentId: string, currentText: string) => {
      const { editingState, textState } = commentService.initializeEditState(
        editingComment,
        editCommentText,
        commentId,
        currentText
      );
      setEditingComment(editingState);
      setEditCommentText(textState);
    },
    [editingComment, editCommentText]
  );

  // Cancel editing a comment
  const cancelEditing = useCallback(
    (commentId: string) => {
      const { editingState, textState } = commentService.clearEditState(
        editingComment,
        editCommentText,
        commentId
      );
      setEditingComment(editingState);
      setEditCommentText(textState);
    },
    [editingComment, editCommentText]
  );

  // Submit edited comment
  const handleEditSubmit = useCallback(
    async (commentId: string) => {
      const text = commentService.prepareCommentForSubmission(
        commentService.getEditText(editCommentText, commentId)
      );
      if (!text) return;

      try {
        await onEditComment(commentId, text);
        cancelEditing(commentId);
      } catch {
        // Failed to edit comment
      }
    },
    [editCommentText, onEditComment, cancelEditing]
  );

  // Delete comment
  const handleDeleteComment = useCallback(
    async (commentId: string) => {
      if (commentService.isDeleting(deletingComment, commentId)) return;

      const newLoadingState = commentService.updateLoadingState(
        deletingComment,
        commentId,
        true
      );
      setDeletingComment(newLoadingState);

      try {
        await onDeleteComment(commentId);
      } catch (error) {
        const resetLoadingState = commentService.updateLoadingState(
          deletingComment,
          commentId,
          false
        );
        setDeletingComment(resetLoadingState);
        throw error;
      }
    },
    [deletingComment, onDeleteComment]
  );

  // Utility function for Enter key submission
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, action: () => void) => {
      commentService.handleKeyboardEvent(e, action);
    },
    []
  );

  // Helper functions
  const isEditingComment = useCallback(
    (commentId: string) => {
      return commentService.isEditing(editingComment, commentId);
    },
    [editingComment]
  );

  const getEditText = useCallback(
    (commentId: string) => {
      return commentService.getEditText(editCommentText, commentId);
    },
    [editCommentText]
  );

  const isDeletingComment = useCallback(
    (commentId: string) => {
      return commentService.isDeleting(deletingComment, commentId);
    },
    [deletingComment]
  );

  const setEditText = useCallback(
    (commentId: string, text: string) => {
      const newTextState = commentService.updateCommentTextState(
        editCommentText,
        commentId,
        text
      );
      setEditCommentText(newTextState);
    },
    [editCommentText]
  );

  return {
    // Main comment form
    commentText,
    setCommentText,
    handleSubmitComment,
    submitComment,
    isSubmitting,

    // Edit state management
    editingComment,
    editCommentText,
    startEditing,
    cancelEditing,
    handleEditSubmit,
    isEditingComment,
    getEditText,
    setEditText,

    // Delete state management
    deletingComment,
    handleDeleteComment,
    isDeletingComment,

    // Utilities
    handleKeyDown,
  };
}
